import { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import {
  faCalendars,
  faHouseMedical,
  IconDefinition,
} from '@fortawesome/pro-light-svg-icons';

import { FaqSearchButton } from './buttons/faq-search-button.component';
import { FaqAssistedShoppingButton } from './buttons/faq-assisted-shopping-button.component';
import { FaqEmailSacButton } from './buttons/faq-email-sac-button.component';
import { FaqLoginButton } from './buttons/faq-login-button.component';
import { FaqUnitButton } from './buttons/faq-unit-button.component';

import { FaqSubItemType } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_types/faq.type';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { Typography } from '@/components/ui/typography/typography.component';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion.component';
import { Card } from '@/components/ui/card.component';
import { Icon } from '@/components/ui/icon.component';
import { FaqCallbackActions } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_types/faq.enum';

interface FaqSubItemProps {
  item: FaqSubItemType;
  isSubitem?: boolean;
}

export const accordionTriggerStyles = tv({
  base: 'border-gray-200',
  variants: {
    isSubitem: {
      true: 'p-0 cursor-default',
    },
    withBorder: {
      true: 'border-t',
    },
  },
});

export const FaqSubItem = ({
  item,
  isSubitem = false,

}: FaqSubItemProps) => {
  const callbackActions = useMemo(
    () => ({
      SEARCH: <FaqSearchButton />,
      ASSISTED_SHOPPING: <FaqAssistedShoppingButton />,
      EMAIL_SAC: <FaqEmailSacButton />,
      LOGIN: <FaqLoginButton />,
      UNIT: <FaqUnitButton />,
    }),
    [],
  );

  const renderItem = (
    item: FaqSubItemType,
    isSubitem: boolean = false,
    icon?: IconDefinition,
  ) => {
    return (
      <AccordionItem key={`key-item-${item.title}`} value={item.title}>
        <AccordionTrigger
          underlineOnHover={false}
          className={accordionTriggerStyles({ isSubitem, className: 'py-3' })}
          showNoIcon={isSubitem}
        >
          <div className="inline-flex flex-start text-start mb-2">
            {icon && (
              <Icon
                className="text-blue-600 self-center items-center w-6 h-6 mr-3"
                icon={icon}
                size="lg"
              />
            )}
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              className="font-bold"
            >
              {item.title}
            </Typography>
          </div>
        </AccordionTrigger>
        <AccordionContent className={isSubitem ? undefined : 'px-6 py-4'}>
          {item.description && <Typography>{item.description}</Typography>}

          {item?.button1 && (
            <div className="flex flex-wrap gap-4 my-5">
              {item?.button1
                && callbackActions[item.button1 as FaqCallbackActions]}
              {item?.button2
                && callbackActions[item.button2 as FaqCallbackActions]}
            </div>
          )}
          {item.subitems && item.subitems.length > 0 && (
            <Card className="my-2">
              <Accordion defaultValue={item.subitems?.[0].title} type="single">
                {renderItem(item.subitems?.[0], true, faCalendars)}
              </Accordion>
              <hr className="my-3" />
              <Accordion defaultValue={item.subitems?.[1].title} type="single">
                {renderItem(item.subitems?.[1], true, faHouseMedical)}
              </Accordion>
            </Card>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return renderItem(item, isSubitem);
};
